import React, { useEffect, useMemo } from 'react';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import { requestModes, sinkContentTypes } from 'new/components/settings/VendorBuilder/selectTypes';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';
import { useVendorBuilder } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import Typography from 'new/elements/Typography';

interface SinkTemplateProps {
  endpointIndex: number;
}

const sinkFields = (endpointIndex) => [
  {
    label: (
      <Typography variant="body2">
        URL<code>sink_template["url.template"]</code>
      </Typography>
    ),
    description: (
      <Typography padding="16px">
        Set the URL to which you wish to send data. <br />
        You can use Nexla system macros like <code>&#123;YYYY&#125;</code>,{' '}
        <code>&#123;MM&#125;</code>`, etc., and the output will be automatically converted into the
        corresponding time values. You can also set dynamic URLs based on the values of an attribute
        in the record by using the macro <code>&#123;record.[attribute-name]&#125;</code>
      </Typography>
    ),
    type: 'text',
    required: true,
    defaultValue: '',
    name: `endpoints[${endpointIndex}].sink_template["url.template"]`,
  },
  {
    label: (
      <>
        Method <code>sink_template.method</code>
      </>
    ),
    description: 'HTTP method used for this rest endpoint. This will usually be POST or PUT',
    type: 'select',
    required: true,
    items: requestModes,
    defaultValue: 'POST',
    name: `endpoints[${endpointIndex}].sink_template.method`,
  },
  {
    label: (
      <>
        Content Type <code>sink_template["content.type"]</code>
      </>
    ),
    description:
      ' Content format used for this rest endpoint. Usually, this will be application/json',
    type: 'select',
    required: true,
    items: sinkContentTypes,
    defaultValue: sinkContentTypes[0].value,
    name: `endpoints[${endpointIndex}].sink_template["content.type"]`,
  },
  {
    label: (
      <>
        Body Template <code>sink_template["body.template"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        <strong>
          Body Template <code>sink_template["body.template"]</code>
        </strong>
        <br />
        <strong>Nexset Record to Payload Template</strong>: In the case of 1 API call per Nexset
        record, you can customize how a Nexset record is added to the API payload. The record itself
        is referenced as a <code>&#123;message.json&#125;</code> macro.
        <br />
        Default and recommended value: <code>&#123;message.json&#125;</code>
        <br />
        <strong>Note</strong>
        <ol>
          <li>Usually, we recommend formatting the API payload with a Nexset transform.</li>{' '}
          <li>
            This property should not be used to format the payload when multiple Nexset records have
            to be batched together into 1 API call. In this case, please use the{' '}
            <code>batch.mode</code> and
            <code>body.transform.function</code> properties.
          </li>
        </ol>
      </Typography>
    ),
    type: 'text',
    defaultValue: '{message.json}',
    required: true,
    name: `endpoints[${endpointIndex}].sink_template["body.template"]`,
  },
  {
    dependsOn: {
      [`endpoints[${endpointIndex}].sink_template["content.type"]`]: 'application/json',
    },
    label: (
      <>
        Would you like to batch your records together? <code>sink_template["batch.mode"]</code>
      </>
    ),
    description:
      'Select this option if you would like to combine multiple records in each API request. You can set the batching algorithm by configuring the related properties.',
    type: 'checkbox',
    defaultValue: false,
    required: true,
    name: `endpoints[${endpointIndex}].sink_template["batch.mode"]`,
  },
  {
    label: (
      <>
        Max records <code>sink_template["max.poll.records"]</code>
      </>
    ),
    type: 'text',
    description:
      'Set the maximum number of records that can be batched together when combining multiple records into an API call.',
    helperText:
      'Set the maximum number of records that can be batched together when combining multiple records into an API call.',
    inputType: 'number',
    dependsOn: {
      [`endpoints[${endpointIndex}].sink_template["batch.mode"]`]: true,
    },
    required: true,
    name: `endpoints[${endpointIndex}].sink_template["max.poll.records"]`,
  },
  {
    label: (
      <>
        Batching Algorithm <code>sink_template["body.transform.function"]</code>
      </>
    ),
    type: 'text',
    description: (
      <Typography padding="16px">
        <strong>
          Batching Algorithm <code>sink_template["body.transform.function"]</code>
        </strong>{' '}
        <br />
        You can define how the platform should combine multiple records in a single API call with a
        Scala function. Use <code>messages</code> to reference records within your Scala function.
        <br />
        <br />
        Sample Scala Functions:
        <ol>
          <li>
            Payload: JSON array of records:
            <pre>"body.transform.function": "messages"</pre>
          </li>
          <li>
            Payload: JSON object containing array of records as a property (e.g., items):
            <pre>"body.transform.function": "Map(\"items\" &#x2192; messages)"</pre>
          </li>
          <li>
            Payload: JSON object with each record modified before batching:
            <pre>
              "body.transform.function": "Map(\"items\" &#x2192; messages.map(m &#x21D2;
              &#123;\nm.put(\"attributes\" , Map(\"type\" &#x2192; \"Account\"))\n m\n&#125;)\n)"
            </pre>
            will result in
            <pre>
              &#123;
              <br />
              "items:[ &#123; ...record1, "attributes": &#123;"type": "Account"&#125;&#125;, &#123;
              ...record2, "attributes": &#123;"type": "Account"&#125;&#125; ]
              <br />
              &#125;
            </pre>
          </li>
        </ol>
      </Typography>
    ),
    dependsOn: {
      [`endpoints[${endpointIndex}].sink_template["batch.mode"]`]: true,
    },
    name: `endpoints[${endpointIndex}].sink_template["body.transform.function"]`,
  },
  {
    label: (
      <>
        (Optional) Create Source? <code>sink_template["create.datasource"]</code>
      </>
    ),
    description:
      'Set this to `true` if you would like to capture the API response for each API call. Responses, along with requests, will be pushed to an autogenerated Nexla webhook.',
    type: 'checkbox',
    defaultValue: false,
    name: `endpoints[${endpointIndex}].sink_template["create.datasource"]`,
  },
];

export const SinkTemplate = (props: SinkTemplateProps) => {
  const { endpointIndex } = props;
  const { updateVendor } = useVendorBuilder();

  useEffect(() => {
    return () => {
      updateVendor((newVendor) => {
        delete newVendor.endpoints[endpointIndex].sink_template;
      });
    };
  }, []);

  const fields = useMemo(() => sinkFields(endpointIndex), [endpointIndex]);

  return (
    <Section
      title={
        <div>
          Endpoint #{endpointIndex + 1}: Sink Template Info{' '}
          <code>vendor_endpoints[{endpointIndex}].sink_template</code>
        </div>
      }
      gap="16px"
    >
      <Typography>
        <strong>Templates for creating destination</strong>
        <ol>
          <li>
            Resource parameters <strong>cannot</strong> begin with <code>rest.iterations</code>
          </li>
          <li>
            If supported by vendor, try using <code>batch.mode</code> for combining a group of
            records into a single call
          </li>
          <li>
            Typically vendors will have same pattern for creating any resource. Try using a single
            endpoint and set resource type as a parameter.
          </li>
          <li>
            If you want to submit an endpoint for updating resources, make sure you set the right
            url pattern for pulling resource id from record into the <code>url.template</code>.
            Search existing vendors for examples.
          </li>
        </ol>
      </Typography>
      <FormBuilder fields={fields} />
    </Section>
  );
};
